import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Empty, Spin, Tooltip } from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined
} from '@ant-design/icons';
import { getChartsService, getGraphViewDataService } from '../../../services/GraphService';
import GraphView from './GraphView';

/**
 * PresentGraphs — interactive "presentation mode" for the active dashboard charts.
 * Displays 2 charts side-by-side at a time with prev/next navigation, dot indicators,
 * keyboard navigation, and optional autoplay for compact executive presentation.
 */

const PresentGraphs = ({ height = 320, dateFrom = null, dateTo = null, overrideDateRange = false }) => {
    const [charts, setCharts] = useState([]);
    const [chartsData, setChartsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [autoplay, setAutoplay] = useState(false);
    const token = localStorage.getItem('token');
    const stageRef = useRef(null);

    const pageSize = 4; // Show 4 charts per slide (2x2 grid)

    const fetchChartData = useCallback(async (chart) => {
        try {
            const config = {
                report_type: chart.report_type,
                x_axis_column: chart.x_axis_column,
                y_axis_columns: chart.y_axis_columns,
                date_column: chart.date_column,
                date_aggregation: chart.date_aggregation,
                // With an active override use exactly the dashboard's range (null = no filter);
                // otherwise fall back to the chart's saved range.
                date_from: overrideDateRange ? (dateFrom || null) : (dateFrom || chart.filters?.date_from),
                date_to: overrideDateRange ? (dateTo || null) : (dateTo || chart.filters?.date_to),
            };
            const response = await getGraphViewDataService(token, config);
            if (response.status === 'success') {
                setChartsData(prev => ({
                    ...prev,
                    [chart.chart_id]: {
                        data: response.data || [],
                        columnTitles: response.column_titles || {}
                    }
                }));
            }
        } catch (error) {
            console.error(`Error fetching data for chart ${chart.chart_id}:`, error);
        }
    }, [token, dateFrom, dateTo, overrideDateRange]);

    const fetchCharts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getChartsService(token);
            if (response.status === 'success') {
                const activeCharts = (response.data || []).filter(c => c.is_active);
                setCharts(activeCharts);
                setLoading(false);
                activeCharts.forEach(chart => fetchChartData(chart));
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error('Failed to load charts:', error);
            setLoading(false);
        }
    }, [token, fetchChartData]);

    useEffect(() => { fetchCharts(); }, [fetchCharts]);

    const total = charts.length;
    const totalPages = Math.ceil(total / pageSize) || 1;

    const go = useCallback((dir) => {
        setPage(prev => {
            if (totalPages === 0) return 0;
            return (prev + dir + totalPages) % totalPages;
        });
    }, [totalPages]);

    // Keyboard navigation
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowRight') go(1);
            else if (e.key === 'ArrowLeft') go(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go]);

    // Autoplay
    useEffect(() => {
        if (!autoplay || totalPages <= 1) return;
        const id = setInterval(() => go(1), 7000);
        return () => clearInterval(id);
    }, [autoplay, totalPages, go]);

    if (loading && total === 0) {
        return (
            <div className="cip-present__empty">
                <Spin tip="Loading presentation..." size="large" />
            </div>
        );
    }

    if (total === 0) {
        return (
            <div className="cip-present__empty">
                <Empty description="No active charts to present. Pin charts from Manage Graphs to see them here." />
            </div>
        );
    }

    const safePage = Math.min(page, totalPages - 1);
    const visibleCharts = charts.slice(safePage * pageSize, safePage * pageSize + pageSize);
    const startNum = safePage * pageSize + 1;
    const endNum = Math.min((safePage + 1) * pageSize, total);

    const companyName = localStorage.getItem('name') || 'Cummins Generator Technologies India Private Limited';
    const iecNo = localStorage.getItem('iec_no') || '0388081627';

    return (
        <div className="cip-present">
            <div className="cip-present__bar">
                <div className="cip-present__meta">
                    <div className="cip-present__title">Executive Dashboard Visuals</div>
                    <div className="cip-present__sub">
                        <span style={{ fontWeight: '700', color: '#1e293b' }}>🏢 {companyName}</span>
                        {iecNo && <span style={{ color: '#475569', fontWeight: '600' }}>(IEC: {iecNo})</span>}
                    </div>
                </div>
                <div className="cip-present__tools">
                    <span className="cip-present__counter">Charts {startNum}-{endNum} of {total}</span>
                    <Tooltip title={autoplay ? 'Pause slideshow' : 'Play slideshow'}>
                        <button
                            type="button"
                            className="cip-present__play"
                            onClick={() => setAutoplay(a => !a)}
                            aria-label={autoplay ? 'Pause slideshow' : 'Play slideshow'}
                        >
                            {autoplay ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                        </button>
                    </Tooltip>
                </div>
            </div>

            <div className="cip-present__stage" ref={stageRef}>
                <button
                    type="button"
                    className="cip-present__nav cip-present__nav--prev"
                    onClick={() => go(-1)}
                    aria-label="Previous charts"
                    disabled={totalPages <= 1}
                >
                    <LeftOutlined />
                </button>

                <div className="cip-present__grid">
                    {visibleCharts.map((chart) => {
                        const cData = chartsData[chart.chart_id];

                        return (
                            <div className="cip-present__card" key={chart.chart_id}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={chart.chart_name}>
                                        {chart.chart_name}
                                    </div>
                                </div>
                                <div style={{ flex: 1, minHeight: '240px' }}>
                                    {cData ? (
                                        <GraphView
                                            chartType={chart.chart_type}
                                            data={cData.data}
                                            columnTitles={cData.columnTitles}
                                            yAxisColumns={chart.y_axis_columns}
                                            height={250}
                                            compact={true}
                                            showMetricOverlay={true}
                                        />
                                    ) : (
                                        <div className="cip-present__loading" style={{ height: 250 }}>
                                            <Spin tip="Loading data..." />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button
                    type="button"
                    className="cip-present__nav cip-present__nav--next"
                    onClick={() => go(1)}
                    aria-label="Next charts"
                    disabled={totalPages <= 1}
                >
                    <RightOutlined />
                </button>
            </div>

            <div className="cip-present__dots" role="tablist" aria-label="Chart pages">
                {Array.from({ length: totalPages }).map((_, pIdx) => (
                    <button
                        key={pIdx}
                        type="button"
                        className={`cip-present__dot ${pIdx === safePage ? 'is-active' : ''}`}
                        onClick={() => setPage(pIdx)}
                        aria-label={`Go to page ${pIdx + 1}`}
                        aria-selected={pIdx === safePage}
                        title={`Page ${pIdx + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default PresentGraphs;
